import React, { useEffect, useMemo, useState } from "react";
import { BoardAvatar, BoardButton } from "../../atoms/BoardDesignSystem";
import "./BoardMemberPicker.scss";

const sameIds = (a = [], b = []) => {
  const sa = [...a].map(Number).sort((x, y) => x - y);
  const sb = [...b].map(Number).sort((x, y) => x - y);
  return sa.length === sb.length && sa.every((id, i) => id === sb[i]);
};

const BoardMemberPicker = ({
  members = [],
  selectedIds = [],
  onChange,
  onClose,
  disabled = false,
}) => {
  const [search, setSearch] = useState("");
  const [draftIds, setDraftIds] = useState(() => selectedIds.map(Number));

  useEffect(() => {
    setDraftIds(selectedIds.map(Number));
  }, [selectedIds]);

  const filteredMembers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return members;
    return members.filter((member) =>
      String(member.name || "").toLowerCase().includes(query)
    );
  }, [members, search]);

  const toggle = (memberId) => {
    if (disabled) return;
    const id = Number(memberId);
    setDraftIds((prev) => {
      const set = new Set(prev.map(Number));
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return [...set];
    });
  };

  const applyAndClose = () => {
    if (!sameIds(draftIds, selectedIds)) {
      onChange?.(draftIds);
    }
    onClose?.();
  };

  const handleClose = () => {
    setDraftIds(selectedIds.map(Number));
    onClose?.();
  };

  return (
    <div className="board-member-picker" onClick={(e) => e.stopPropagation()}>
      <div className="board-member-picker__header">
        <h4 className="board-member-picker__title">Members</h4>
        {onClose && (
          <button
            type="button"
            className="board-member-picker__close"
            onClick={handleClose}
            aria-label="Close"
          >
            ✕
          </button>
        )}
      </div>

      <input
        type="search"
        className="board-member-picker__search"
        placeholder="Search members..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onMouseDown={(e) => e.stopPropagation()}
        disabled={disabled}
      />

      <p className="board-member-picker__hint">Select one or more members</p>

      <div className="board-member-picker__list">
        {filteredMembers.length === 0 && (
          <p className="board-member-picker__empty">No members found.</p>
        )}
        {filteredMembers.map((member) => {
          const memberId = Number(member.id || member.userId);
          const checked = draftIds.map(Number).includes(memberId);
          return (
            <button
              key={memberId}
              type="button"
              className={`board-member-picker__row ${checked ? "board-member-picker__row--active" : ""}`}
              onClick={() => toggle(memberId)}
              disabled={disabled}
            >
              <span
                className={`board-member-picker__check ${checked ? "board-member-picker__check--checked" : ""}`}
                aria-hidden="true"
              >
                {checked && <span className="board-member-picker__check-icon">✓</span>}
              </span>
              <BoardAvatar
                name={member.name}
                userId={memberId}
                imageUrl={member.avatarUrl || member.imageUrl}
                size="sm"
              />
              <span className="board-member-picker__name">{member.name}</span>
            </button>
          );
        })}
      </div>

      <BoardButton
        variant="primary"
        block
        size="sm"
        onClick={applyAndClose}
        disabled={disabled}
      >
        Done
      </BoardButton>
    </div>
  );
};

export default BoardMemberPicker;
